/**
 * ClosureRequestPanel — T-42 project closure approval, rendered inside the Settings tab's
 * "Project Closure" card (ProjectDetailPage → ClosureSection).
 *
 *  - PM / SA (requesters): "Request closure" with a required reason (10–1000 chars). Once raised,
 *    shows "Closure requested by X on Y — awaiting leadership" instead of the form.
 *  - Leadership / admin (approvers): when a request is pending, shows it inline with Approve /
 *    Reject (note). Their direct "Close Project" button stays in ClosureSection.
 *
 * The server is the enforcer (PATCH status=Closed by a PM → 403 CLOSURE_REQUIRES_APPROVAL); this
 * panel only routes each role to the path it is allowed to use.
 */
import { useId, useState } from "react";
import { toast } from "sonner";
import { Clock, Loader2 } from "lucide-react";

import type { Project } from "@/types";
import {
  useCreateClosureRequest,
  useProjectClosureRequest,
} from "@/hooks/useClosureRequests";
import { apiErrorMessage } from "@/lib/api";
import { formatDateValue } from "@/lib/date-change";
import { ClosureRequestCard } from "@/components/leadership/ClosureRequestCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

/** Mirror CLOSURE_REASON_MIN / MAX in packages/projects/services/project-closure.service.ts. */
export const CLOSURE_REASON_MIN = 10;
export const CLOSURE_REASON_MAX = 1000;

export interface ClosureRequestPanelProps {
  project: Pick<Project, "jira_key" | "status"> &
    Partial<Pick<Project, "viewer_is_member">>;
  /** 'requester' = PM / SA; 'approver' = leadership / admin. */
  mode: "requester" | "approver";
}

export function ClosureRequestPanel({
  project,
  mode,
}: ClosureRequestPanelProps): JSX.Element | null {
  const isClosed = project.status === "Closed";
  // The server only accepts closure requests from the project's own team. Requester mode is PM / SA
  // only (leadership/admin get approver mode and close directly), so no role bypass applies here.
  // `viewer_is_member` absent (older payload / tests) keeps the form so nothing regresses.
  const notMember =
    mode === "requester" && project.viewer_is_member === false;
  const pendingQuery = useProjectClosureRequest(project.jira_key, !isClosed);
  const create = useCreateClosureRequest(project.jira_key);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const reasonId = useId();
  const helpId = `${reasonId}-help`;
  const errorId = `${reasonId}-error`;

  if (isClosed) {
    return mode === "requester" ? (
      <p className="text-sm text-text-secondary">
        This project is closed. Only leadership can reopen it.
      </p>
    ) : null;
  }

  if (pendingQuery.isLoading) {
    return (
      <Skeleton
        className="h-24 w-full rounded-lg"
        aria-label="Loading closure request"
      />
    );
  }

  const pending = pendingQuery.data ?? null;

  if (mode === "approver") {
    if (!pending) return null;
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-text-primary">
          Closure requested — awaiting your decision
        </p>
        <ClosureRequestCard request={pending} hideProject />
      </div>
    );
  }

  if (notMember && !pending) {
    return (
      <p className="text-sm text-text-secondary">
        Only members of this project&apos;s team can request closure. Ask leadership to add you to
        the team, or to close the project.
      </p>
    );
  }

  if (pending) {
    return (
      <Alert role="status">
        <Clock className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Closure requested</AlertTitle>
        <AlertDescription>
          Closure requested by{" "}
          {pending.requested_by_email ?? pending.requested_by} on{" "}
          {formatDateValue(pending.created_at)} — awaiting leadership.
        </AlertDescription>
      </Alert>
    );
  }

  const trimmed = reason.trim();
  const tooShort = trimmed.length < CLOSURE_REASON_MIN;
  const showError = touched && tooShort;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setTouched(true);
    if (tooShort) return;
    create.mutate(
      { reason: trimmed },
      {
        onSuccess: () => {
          toast.success("Closure requested — leadership has been notified");
          setReason("");
          setTouched(false);
        },
        onError: (err) =>
          toast.error(apiErrorMessage(err, "Failed to request closure")),
      },
    );
  };

  return (
    <form className="space-y-2" onSubmit={handleSubmit} noValidate>
      <Label htmlFor={reasonId}>Reason for closure *</Label>
      <Textarea
        id={reasonId}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        onBlur={() => setTouched(true)}
        rows={3}
        maxLength={CLOSURE_REASON_MAX}
        placeholder="e.g. All deliverables accepted and customer sign-off received."
        aria-required="true"
        aria-invalid={showError ? true : undefined}
        aria-describedby={showError ? `${helpId} ${errorId}` : helpId}
      />
      <p id={helpId} className="text-xs text-text-secondary">
        Leadership approves closure. At least {CLOSURE_REASON_MIN} characters.
      </p>
      {showError && (
        <p id={errorId} role="alert" className="text-sm text-error">
          Enter a reason of at least {CLOSURE_REASON_MIN} characters.
        </p>
      )}
      <Button type="submit" variant="outline" disabled={create.isPending}>
        {create.isPending && (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        Request closure
      </Button>
    </form>
  );
}

export default ClosureRequestPanel;
